import { FC, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, TextField, Tooltip, Typography } from '@mui/material';
import styles from 'features/task/ui/MessagesComponent.module.css';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import Loader from 'shared/loader/Loader';
import { CreateMessage, Download, FileData, FileName, isLoadingCreateMessage, isLoadingTaskMessages, TaskInfo, TaskMessages } from 'entities/task-new/selector';
import PersonIcon from '@mui/icons-material/Person';
import { resetFileData } from 'entities/task-new';
import { fetchFileDownload, fetchSendMessage, fetchTaskFiles, fetchTaskMessages } from 'entities/task-new/actions/fetch-task-case';
import { useLocation } from 'react-router-dom';
import { useForm } from 'shared/utils/hooks/useForm';
import { validationSchema } from '../utils/validationSchema';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import * as Yup from 'yup';
import CustomButton from 'shared/custom-button/CustomButton';
import { getRequest } from 'shared/utils/fetch-api';
import { acceptedMimeTypes, handleFileChange } from '../utils/handleFileChange';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import { fetchUnreadMessages } from 'entities/unread-messages/actions/fetch-unread-messages';

type FileItem = {
    Name: string;
    DataBase64: string;
};

export const MessagesComponent: FC = () => {
    const taskMessages = useAppSelector(TaskMessages);
    const isLoading = useAppSelector(isLoadingTaskMessages);
    const dispatch = useAppDispatch();
    const location = useLocation();
    const useQuery = () => new URLSearchParams(useLocation().search);
    const query = useQuery();
    const idTask = query.get('id');

    const formatDateTime = (isoString: string): string => {
        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${day}.${month}.${year} в ${hours}:${minutes}:${seconds}`;
    };

    const fileData = useAppSelector(FileData);
    const fileName = useAppSelector(FileName);
    const isDownload = useAppSelector(Download);

    useEffect(() => {
        if (fileData) {
            const link = document.createElement('a');
            link.href = `data:application/octet-stream;base64,${fileData}`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            dispatch(resetFileData());
        }
    }, [fileData, isDownload, fileName, dispatch]);

    const handleDownload = async (fileId: string) => {
        if (idTask) {
            try {
                await dispatch(fetchFileDownload({
                    TaskID: idTask,
                    FileID: fileId
                }));
            } catch (error) {
                console.error('Error downloading the file:', error);
            }
        }
    };

    const formInput = {
        Text: '',
        Files: [],
        TaskID: idTask,
    };

    const {
        formData,
        handleChangeInput,
        errors,
        setErrors,
        handleRemoveFile,
        setFormData
    } = useForm(formInput, validationSchema);

    const textFieldRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (textFieldRef.current) {
            textFieldRef.current.style.height = 'auto';
            textFieldRef.current.style.height = `${textFieldRef.current.scrollHeight}px`;
        }
    }, [formData.Text]);

    const [fileError, setFileError] = useState<string | null>(null);
    const [isCompressing, setIsCompressing] = useState(false);
    const [open, setOpen] = useState(false);

    const validateForm = async (formData: any) => {
        try {
            await validationSchema.validate(formData, { abortEarly: false });
            return null;
        } catch (err) {
            if (err instanceof Yup.ValidationError) {
                const errors: { [key: string]: string } = {};
                err.inner.forEach((error) => {
                    if (error.path) {
                        errors[error.path] = error.message;
                    }
                });
                return errors;
            }
            return null;
        }
    };

    const [successSendMessage, setSuccessSendMessage] = useState(false);
    const createMessage = useAppSelector(CreateMessage);
    const isLoadingMessage = useAppSelector(isLoadingCreateMessage);

    const handleSendMessage = async () => {
        if (idTask) {
            const validationErrors = await validateForm(formData);
            if (validationErrors) {
                setErrors(validationErrors);
                return;
            }
            dispatch(fetchSendMessage({
                TaskID: formData.TaskID,
                Files: formData.Files,
                Text: formData.Text,
                ToRework: false
            })).then(() => {
                if (createMessage.errorCreateMessage.length === 0) {
                    setSuccessSendMessage(true);
                }
                dispatch(fetchTaskMessages({ TaskID: idTask }));
                dispatch(fetchTaskFiles({ TaskID: idTask }));
                setFormData((prev: any) => ({
                    Text: '',
                    Files: [],
                    TaskID: idTask,
                }));
                setErrors({});
            });
        }
    };


    const [errorsRead, setErrorsRead] = useState([]);
     useEffect(() => {
        const unreadMessageIds = taskMessages.Messages?.filter((message) => !message.Read).map((message) => message.ID);
        if (unreadMessageIds && unreadMessageIds.length > 0) {
            const timer = setTimeout(() => {
                handleRead();
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [taskMessages.Messages, idTask]); 
    
    const handleRead = async (): Promise<void> => {
        if (idTask) {
            const unreadMessageIds = taskMessages.Messages.filter((message) => !message.Read).map((message) => message.ID);
            try {
                const response = await getRequest({
                    "TaskID": idTask,
                    "MessageIDs": unreadMessageIds
                }, 'dms-hr', 'setTaskMessagesRead', 'POST');
                const result = await response.json();
                if (result.Errors.length > 0) {
                    setErrorsRead(result.Errors);
                } else {
                    dispatch(fetchTaskMessages({ TaskID: idTask })).then(() => dispatch(fetchUnreadMessages()));
                }
            } catch (error) {
                console.error(error);
            }
        }
    };

    return (
        <Box>
            {isLoading && (
                <Loader title='Загрузка...' />
            )}
            {!isLoading && (
                <>
                    {taskMessages.Messages.filter((message) => !message.Read).length > 0 && (
                        <Box className={styles.readBtnBox}>
                            <CustomButton
                                title="Прочитать все"
                                icon={DoneAllIcon}
                                leftIcon={true}
                                handleClick={handleRead}
                                textColor="#0054E6"
                                backgroundColor="none"
                                gap="10px"
                                border='none'
                                borderRadius='4px'
                                padding="8px 30px"
                            />
                        </Box>

                    )}
                    <Box className={styles.messagesBox}>
                        {taskMessages.Messages.length > 0 ? (
                            <>
                                {taskMessages.Messages.map((item) => (
                                    <Box
                                        key={item.ID}
                                        className={`${styles.message} ${item.IsOutgoing ? styles.outgoing : styles.incoming} ${!item.Read && styles.unreadMessage}`}
                                    >
                                        <p className={styles.messagePerson}><span><PersonIcon /></span>{item.AuthorName}</p>
                                        <p className={styles.messageText}>{item.Text}</p>
                                        <p className={styles.messageData}>{formatDateTime(item.Date)} <span>{item.Read ? <DoneAllIcon /> : <DoneIcon />}</span></p>
                                        <Box className={styles.messageFiles}>
                                            {item.Files.map((file) => (
                                                <a
                                                    key={file.ID}
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleDownload(file.ID);
                                                    }}
                                                >
                                                    {file.Name}
                                                </a>
                                            ))}
                                        </Box>
                                    </Box>
                                ))}
                            </>
                        ) : (
                            <Box className={styles.emptyBox}>
                                <p>Пока нет ни одного сообщения</p>
                            </Box>
                        )}
                    </Box>
                    <Box className={styles.formMessage}>
                        <Box className={styles.inputBox}>
                            <input
                                accept={acceptedMimeTypes.join(',')}
                                style={{ display: 'none' }}
                                id="file-upload"
                                type="file"
                                multiple
                                onChange={(e) => handleFileChange(e, formData, setFormData, setFileError, setIsCompressing)}
                                disabled={isCompressing}
                            />
                            <label htmlFor="file-upload">
                                <Tooltip title="Добавить файл" placement="top">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        disabled={isCompressing}
                                    >
                                        {isCompressing ? 'Обработка...' : <AttachFileIcon />}
                                    </Button>
                                </Tooltip>
                            </label>

                            {fileError && (
                                <Typography color="error" sx={{ mt: 1 }}>
                                    {fileError}
                                </Typography>
                            )}
                            <TextField
                                inputRef={textFieldRef}
                                label="Текст сообщения"
                                multiline
                                rows={1}
                                value={formData.Text}
                                name="Text"
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChangeInput({ event })}
                                variant="outlined"
                                error={!!errors.Text}
                                helperText={errors.Text || ''}
                                sx={{
                                    width: '80%',
                                    '& .MuiInputBase-root': {
                                        height: 'auto',
                                        alignItems: 'flex-start',
                                        minHeight: '40px'
                                    },
                                }}
                            />
                            <Tooltip title="Отправить сообщение" placement="top">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    onClick={handleSendMessage}
                                    disabled={isLoadingMessage}
                                >
                                    {isLoadingMessage ? 'Отправка' : <SendIcon />}
                                </Button>
                            </Tooltip>
                        </Box>
                        <Box mt={1}>
                            {formData.Files?.map((file: FileItem, index: number) => (
                                <Chip
                                    key={index}
                                    label={file.Name}
                                    onDelete={() => handleRemoveFile(index)}
                                    deleteIcon={<DeleteIcon />}
                                    variant="outlined"
                                />
                            ))}
                        </Box>


                        {successSendMessage && (
                            <Box className={styles.successText}>
                                <p>Сообщение отправлено!</p>
                            </Box>
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default MessagesComponent;